import { SortableContainer, SortableElement } from "react-sortable-hoc";
import DraggableRow from "./DraggableRow";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import { CourseSelection } from "./CourseSelection";
import { Dialog } from "../../../components/Dialog";

const SortableItem = SortableElement<any>(
  ({
    value,
    number,
    total,
    onDelete,
    disabled,
  }: {
    value: any;
    number: number;
    total: number;
    onDelete: any;
    disabled?: boolean;
  }) => (
    <DraggableRow
      data={value}
      number={number}
      total={total}
      onDelete={onDelete}
    />
  )
);

const SortableList = SortableContainer<any>(({ items, onDelete }: any) => {
  return (
    <div className="flex flex-col gap-3">
      {items?.map((value: any, index: number) => (
        <SortableItem
          key={`item-${value.video_id} ${index}`}
          index={index}
          value={value}
          number={index + 1}
          total={items?.length}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
});

const arrayMoveMutate = ({ array, from, to }: any) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};

const arrayMove = ({ array, from, to }: any) => {
  array = array.slice();
  arrayMoveMutate({ array, from, to });
  return array;
};

export const targetHasProp = (
  target: Element | null,
  hasProp: (target: Element) => boolean
): boolean => {
  while (target) {
    if (hasProp(target)) {
      return true;
    }
    target = target.parentElement;
  }
  return false;
};

export function SubVideoList({
  playlist,
  onAdd,
  onDelete,
  onUpdate,
}: {
  playlist: {
    playlist_id: string;
    playlist_title: string;
    playlist_description: string;
    playlist_image: string;
    publisher_id: string;
    videos: Array<{
      video_id: string;
      video_title: string;
      video_description: string | null;
      duration: number;
      short_video: string;
      short_video_hls: string;
      trailer_thumb_image: string;
      is_landscape: number;
      thumb_image: string;
    }>;
  };
  onAdd: () => void;
  onDelete: (video: any) => void;
  onUpdate?: (videos: any[]) => void;
}) {
  const [videos, setVideos] = useState(playlist.videos || []);
  const [editVideoPopupOpen, setEditVideoPopupOpen] = useState<any>(false);
  const [isLoading, setLoading] = useState(false);

  const mutate = useCallback(
    (videoIds: string[]) => {
      let obj = {
        video_ids: videoIds,
      };
      console.log("Sorted video IDs:", videoIds);
      api
        .updatePlaylist(playlist.playlist_id, obj)
        .then(() => {
          ShowToast({ message: "Videos reordered", type: "success" });
          setLoading(false);
          onUpdate && onUpdate(videos);
        })
        .catch((err) =>
          ShowToast({
            message: err?.response?.data?.message || "Error reordering",
            type: "error",
          })
        );
    },
    [playlist.playlist_id]
  );

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      if (isLoading) return;
      if (oldIndex === newIndex) return; // No change
      const reordered = arrayMove({
        array: videos,
        from: oldIndex,
        to: newIndex,
      });
      const oldIds = videos.map((v) => v.video_id);
      const newIds = reordered.map((v) => v.video_id);
      // Only update if order actually changed
      const isChanged = oldIds.some((id, idx) => id !== newIds[idx]);
      if (!isChanged) return;
      setLoading(true);
      setVideos(reordered);
      mutate(newIds);
    },
    [videos, mutate, onUpdate, isLoading]
  );

  const shouldCancelSortStart = (e: any): boolean => {
    return targetHasProp(e.target as Element, (el) =>
      ["button"].includes(el.tagName.toLowerCase())
    );
  };

  useEffect(() => {
    setVideos(playlist.videos || []);
  }, [playlist]);

  const updateApis = (video_ids: any[]) => {
    setLoading(true);
    api
      .updatePlaylist(playlist.playlist_id, {
        video_ids: video_ids.filter((item) => item && item !== ""),
      })
      .then(() => {
        ShowToast({ message: "Videos updated", type: "success" });
        setLoading(false);
        setEditVideoPopupOpen(false);
        onAdd();
      })
      .catch((err) => {
        ShowToast({
          message: err?.response?.data?.message || "Error updating videos",
          type: "error",
        });
        setLoading(false);
      });
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="h-[22px] justify-between items-center flex w-full mb-4">
        <div className="text-black text-lg font-bold font-['Montserrat']">
          Videos ({videos?.length})
        </div>
        <button
          onClick={onAdd}
          className="text-[#355e3b] text-[13px] font-medium font-['Montserrat']"
          disabled={isLoading}
        >
          + Add another video
        </button>
      </div>

      <div className="relative min-h-[120px]">
        {!editVideoPopupOpen ? (
          <SortableList
            items={videos}
            onSortEnd={onSortEnd}
            shouldCancelStart={shouldCancelSortStart}
            onDelete={onDelete}
            lockAxis="y"
            lockToContainerEdges={true}
            helperClass="dragging"
          />
        ) : (
          <Dialog
            isOpen={editVideoPopupOpen}
            onClose={() => setEditVideoPopupOpen(false)}
            className="w-[800px] bg-white"
            title={"Select Courses"}
          >
            <CourseSelection
              loading={isLoading}
              onBack={() => setEditVideoPopupOpen(false)}
              onSubmit={(selectedCourses) => {
                setVideos(selectedCourses);
                let ar = selectedCourses?.map((item) => item?.video_id);
                updateApis(ar);
              }}
              selectedCourses={videos}
            />
          </Dialog>
        )}
      </div>
    </div>
  );
}
